import { computed, Ref, ref } from 'vue';

export const modals = {
  NEW_USER: 'NEW_USER',
  CREATE_SINGLE_USER: 'CREATE_SINGLE_USER',
  CREATE_MULTIPLE_USERS: 'CREATE_MULTIPLE_USERS',
  EDIT_USER: 'EDIT_USER',
  MANAGE_USER_LOGIN_INFO: 'MANAGE_USER_LOGIN_INFO',
  MANAGE_USER_GROUPS: 'MANAGE_USER_GROUPS',
  MANAGE_USER_ORGANIZATIONS: 'MANAGE_USER_ORGANIZATIONS',
  CREATE_LOGIN_CARD: 'CREATE_LOGIN_CARD',
  CREATE_MULTIPLE_LOGIN_CARDS: 'CREATE_MULTIPLE_LOGIN_CARDS',
  DELETE_USER: 'DELETE_USER',
  CONFIRM_DELETE_USERS: 'CONFIRM_DELETE_USERS',
  CONFIRM_DELETE_USER_QR_CODE: 'CONFIRM_DELETE_USER_QR_CODE',
  DELETE_GROUPS: 'DELETE_GROUPS',
  DELETE_GROUP: 'DELETE_GROUP',
  EDIT_GROUP: 'EDIT_GROUP',
  EDIT_GROUP_LOGIN: 'EDIT_GROUP_LOGIN',
  CONFIRM_DELETE_GROUP_CODE: 'CONFIRM_DELETE_GROUP_CODE',
  CONFIRM_DELETE_MEMBERSHIP_FROM_GROUP: 'CONFIRM_DELETE_MEMBERSHIP_FROM_GROUP',
  CONFIRM_DELETE_MEMBERSHIP_FROM_USER: 'CONFIRM_DELETE_MEMBERSHIP_FROM_USER',
  SET_GROUP_PASSWORD: 'SET_GROUP_PASSWORD',
  UPLOAD_USERS_TO_CREATE: 'UPLOAD_USERS_TO_CREATE',
  CONFLICT_RESOLVE: 'CONFLICT_RESOLVE',
  RESOLVE_DUPLICATE_CONFLICT: 'RESOLVE_DUPLICATE_CONFLICT',
  RESOLVE_INVALID_GROUP_CONFLICT: 'RESOLVE_INVALID_GROUP_CONFLICT',
  RESOLVE_EXISTS_CONFLICT: 'RESOLVE_EXISTS_CONFLICT',
  NEW_GROUP: 'NEW_GROUP',
  MANAGE_GROUP_MEMBERS: 'MANAGE_GROUP_MEMBERS',
  CONFIRM_UNSAVED_CHANGES: 'CONFIRM_UNSAVED_CHANGES',
  IMPORT_USERS: 'IMPORT_USERS',
  MAIN_TABLE_FILTERS: 'MAIN_TABLE_FILTERS',
  MANAGE_CONTENT_SETTINGS: 'MANAGE_CONTENT_SETTINGS',
  USER_EXISTS_CONFLICT: 'USER_EXISTS_CONFLICT',
} as const;

type Keys = keyof typeof modals;
export type ModalTypes = (typeof modals)[Keys];

type Context = {
  onClose?: () => void;
  // set by a modal that opened this one, so its cancel can return there
  onBack?: () => void;
};
type ModalContextType = {
  [V in ModalTypes]: Context;
} & {
  CREATE_LOGIN_CARD: Context & { createQrCodeCheck?: boolean; hideBackButton?: boolean };
  CREATE_MULTIPLE_LOGIN_CARDS: Context & { fileName?: string };
  DELETE_USER: Context & {
    organizationUids?: string[];
    onDeleted?: () => void;
    onFailure?: () => void;
  };
};

const isOpen: Ref<Record<string, boolean>> = ref({});
const context = ref<Record<string, Context>>({});
const useModal = <T extends ModalTypes>(name: T) => {
  isOpen.value = { ...isOpen.value, [name]: isOpen.value[name] ?? false };

  const open = (p?: ModalContextType[T]) => {
    context.value[name] = p ?? {};
    isOpen.value = { ...isOpen.value, [name]: true };
  };

  const close = () => {
    isOpen.value = { ...isOpen.value, [name]: false };
    context.value[name]?.onClose?.();
  };

  return {
    isOpen: computed(() => isOpen.value[name] ?? false),
    context: computed((): ModalContextType[T] => (context.value[name] ?? {}) as ModalContextType[T]),
    open,
    close,
  };
};

export default useModal;
